import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { LanguageProvider } from "@/context/LanguageContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthCallback from "@/components/AuthCallback";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import Directory from "@/pages/Directory";
import HcahpsLeaderboard from "@/pages/HcahpsLeaderboard";
import GenomeLanding from "@/pages/genome/GenomeLanding";
import GenomeStart from "@/pages/genome/GenomeStart";
import GenomeMap from "@/pages/genome/GenomeMap";
import DoctorPublicProfile from "@/pages/DoctorPublicProfile";
import HospitalPublicProfile from "@/pages/HospitalPublicProfile";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DPDP from "@/pages/DPDP";
import Grievance from "@/pages/Grievance";
import Network from "@/pages/Network";
import LeaderProfile from "@/pages/LeaderProfile";
import Startups from "@/pages/Startups";
import StartupProfile from "@/pages/StartupProfile";

import PatientDashboard from "@/pages/patient/PatientDashboard";
import PatientTriage from "@/pages/patient/PatientTriage";
import PatientRecords from "@/pages/patient/PatientRecords";
import PatientAppointments from "@/pages/patient/PatientAppointments";

import DoctorDashboard from "@/pages/doctor/DoctorDashboard";
import DoctorAppointments from "@/pages/doctor/DoctorAppointments";
import DoctorProfile from "@/pages/doctor/DoctorProfile";
import DoctorAnalytics from "@/pages/doctor/DoctorAnalytics";

import HospitalDashboard from "@/pages/hospital/HospitalDashboard";
import HospitalIncidents from "@/pages/hospital/HospitalIncidents";
import HospitalTrust from "@/pages/hospital/HospitalTrust";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminVerification from "@/pages/admin/AdminVerification";
import AdminGrievances from "@/pages/admin/AdminGrievances";
import AdminErrors from "@/pages/admin/AdminErrors";
import AdminGenome from "@/pages/admin/AdminGenome";
import AdminCroInvestigators from "@/pages/admin/AdminCroInvestigators";
import AdminDispenses from "@/pages/admin/AdminDispenses";
import Forevency from "@/pages/forevency/Forevency";
import ClinicGrowth from "@/pages/clinic-growth/ClinicGrowth";
import VerifyRx from "@/pages/VerifyRx";

import PrivacySettings from "@/pages/settings/PrivacySettings";
import Upgrade from "@/pages/Upgrade";
import Billing from "@/pages/Billing";
import CroDashboard from "@/pages/cro/CroDashboard";
import CroInputs from "@/pages/cro/CroInputs";
import CookieBanner from "@/components/CookieBanner";
import ContentProtection from "@/components/ContentProtection";
import ErrorBoundary from "@/components/ErrorBoundary";
import { installGlobalErrorHandlers } from "@/lib/errorReporter";

installGlobalErrorHandlers();

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/directory" element={<Directory />} />
      <Route path="/hcahps" element={<HcahpsLeaderboard />} />
      <Route path="/genome" element={<GenomeLanding />} />
      <Route
        path="/genome/start"
        element={
          <ProtectedRoute>
            <GenomeStart />
          </ProtectedRoute>
        }
      />
      <Route
        path="/genome/map"
        element={
          <ProtectedRoute>
            <GenomeMap />
          </ProtectedRoute>
        }
      />
      <Route path="/doctors/:id" element={<DoctorPublicProfile />} />
      <Route path="/hospitals/:id" element={<HospitalPublicProfile />} />
      <Route path="/network" element={<Network />} />
      <Route path="/network/leaders/:id" element={<LeaderProfile />} />
      <Route path="/startups" element={<Startups />} />
      <Route path="/startups/:id" element={<StartupProfile />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/dpdp" element={<DPDP />} />
      <Route path="/grievance" element={<Grievance />} />

      {/* Subscription */}
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute roles={["patient", "doctor", "hospital"]}>
            <Upgrade />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute roles={["patient", "doctor", "hospital"]}>
            <Billing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute roles={["patient", "doctor", "hospital"]}>
            <Billing />
          </ProtectedRoute>
        }
      />

      {/* CRO / Clinical Research Intelligence (Phase 1) */}
      <Route
        path="/cro"
        element={
          <ProtectedRoute roles={["patient", "doctor", "hospital", "admin"]}>
            <CroDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cro/inputs"
        element={
          <ProtectedRoute roles={["patient", "doctor", "hospital", "admin"]}>
            <CroInputs />
          </ProtectedRoute>
        }
      />

      {/* Settings — shared across roles */}
      <Route
        path="/settings/privacy"
        element={
          <ProtectedRoute>
            <PrivacySettings />
          </ProtectedRoute>
        }
      />

      {/* Patient */}
      <Route
        path="/patient"
        element={
          <ProtectedRoute roles={["patient"]}>
            <PatientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patient/triage"
        element={
          <ProtectedRoute roles={["patient"]}>
            <PatientTriage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patient/records"
        element={
          <ProtectedRoute roles={["patient"]}>
            <PatientRecords />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patient/appointments"
        element={
          <ProtectedRoute roles={["patient"]}>
            <PatientAppointments />
          </ProtectedRoute>
        }
      />

      {/* Doctor */}
      <Route
        path="/doctor"
        element={
          <ProtectedRoute roles={["doctor"]}>
            <DoctorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/doctor/profile"
        element={
          <ProtectedRoute roles={["doctor"]}>
            <DoctorProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/doctor/appointments"
        element={
          <ProtectedRoute roles={["doctor"]}>
            <DoctorAppointments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/doctor/analytics"
        element={
          <ProtectedRoute roles={["doctor"]}>
            <DoctorAnalytics />
          </ProtectedRoute>
        }
      />

      {/* Hospital */}
      <Route
        path="/hospital"
        element={
          <ProtectedRoute roles={["hospital"]}>
            <HospitalDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospital/incidents"
        element={
          <ProtectedRoute roles={["hospital"]}>
            <HospitalIncidents />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hospital/trust"
        element={
          <ProtectedRoute roles={["hospital"]}>
            <HospitalTrust />
          </ProtectedRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/verification"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminVerification />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/grievances"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminGrievances />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/errors"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminErrors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/genome"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminGenome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cro-investigators"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminCroInvestigators />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dispenses"
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminDispenses />
          </ProtectedRoute>
        }
      />
      <Route path="/verify/rx/:token" element={<VerifyRx />} />
      <Route
        path="/forevency"
        element={
          <ProtectedRoute roles={["doctor", "hospital", "admin"]}>
            <Forevency />
          </ProtectedRoute>
        }
      />
      <Route
        path="/patient-reach"
        element={
          <ProtectedRoute roles={["doctor", "hospital", "admin"]}>
            <Forevency />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clinic-growth"
        element={
          <ProtectedRoute roles={["doctor", "hospital", "admin"]}>
            <ClinicGrowth />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <SubscriptionProvider>
                <AppRouter />
                <ContentProtection />
                <CookieBanner />
                <Toaster position="top-right" richColors />
              </SubscriptionProvider>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}
